'use client';

import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import patches from '@/util/intlPatches';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';

/**
 * @param {{
 *  value?:number
 *  currencyCode?:string
 *  specialPrice?:number
 *  classes?:{specialPrice?:string}
 *  showFractionDigits?:boolean
 * }} props
 */
const Price = props => {
  const { locale } = useIntl();
  const { value, currencyCode, specialPrice, classes = {}, showFractionDigits = true } = props;

  const isPriceValid =
    ((typeof value === 'number' && !isNaN(value)) ||
      (typeof value === 'string' && !!value.trim() && !isNaN(Number(value)))) &&
    !!currencyCode;
  const isSpecialPriceValid =
    ((typeof specialPrice === 'number' && !isNaN(specialPrice)) ||
      (typeof specialPrice === 'string' && !!specialPrice.trim() && !isNaN(Number(specialPrice)))) &&
    !!currencyCode;

  const renderChildrenPart = parts => {
    return parts.map((part, i) => {
      const key = `${i}-${part.value}`;

      return <span key={key}>{part.value}</span>;
    });
  };

  const renderPrice = (price, isValid) => {
    if (!isValid) return null;

    const parts = patches.toParts.call(
      new Intl.NumberFormat(
        websiteEnv.isCanadaFrench || currencyCode === 'MXN' ? 'en-US' : locale,
        Object.assign(
          {
            style: 'currency',
            currency: currencyCode,
          },
          showFractionDigits ? {} : { minimumFractionDigits: 0, maximumFractionDigits: 0 },
        ),
      ),
      price,
    );

    if (currencyCode !== 'EUR') {
      return renderChildrenPart(parts);
    }

    const isCurrencySymbolAtTheEnd = parts[parts.length - 1]?.type === 'currency';

    if (isCurrencySymbolAtTheEnd) {
      return renderChildrenPart(parts);
    }

    const currencyInfo = parts.find(x => x?.type === 'currency');

    if (!currencyInfo) return renderChildrenPart(parts);

    const nextParts = parts.filter(x => x?.type !== 'currency').concat({ type: 'literal', value: ' ' }, currencyInfo);

    if (nextParts[0].type === 'literal' && typeof nextParts[0].value === 'string' && nextParts[0].value.trim() === '') {
      nextParts.shift();
    }

    return renderChildrenPart(nextParts);
  };

  const specialPriceChildren = renderPrice(specialPrice, isSpecialPriceValid);

  return (
    <Fragment>
      {renderPrice(value, isPriceValid)}
      {value < specialPrice && specialPriceChildren ? (
        <span className={cn('ml-[7px] text-sm leading-5 text-aba6a0 line-through', classes.specialPrice)}>
          {specialPriceChildren}
        </span>
      ) : null}
    </Fragment>
  );
};

export default Price;
